// AddMonthlyParkingSubscriptionModal.js
import React, { useState, useMemo, useEffect } from "react";
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Label,
    Input,
    Row,
    Col,
    InputGroup,
    InputGroupText
} from "reactstrap";
import Select from "react-select";
import PropTypes from "prop-types";
import { addMonthlyParkingSubscription } from "helpers/fakebackend_helper";

const AddMonthlyParkingSubscriptionModal = ({ isOpen, toggle, orders, reloadTime, handleSave }) => {
    const user = JSON.parse(localStorage.getItem("authUser"));
    const today = new Date().toISOString().slice(0, 10);
    const [lineItems, setLineItems] = useState([]);
    const [date, setDate] = useState(today);
    const [amount, setAmount] = useState(0);
    const [paymentType, setPaymentType] = useState("cod");
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [isNewCustomer, setIsNewCustomer] = useState(false);
    const [selectedLocation, setSelectedLocation] = useState(null);
    const [coupon, setCoupon] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [newCustomer, setNewCustomer] = useState({
        first_name: "",
        last_name: "",
        customer_email: "",
        customer_phone: "",
        license_plates: "",
        car_models: "",
        company_name: ""
    });

    // Extract unique customers
    const customerOptions = useMemo(() => {
        const seen = new Set();
        const opts = [];

        opts.push({
            value: "new",
            label: "+ New Customer"
        });

        // (orders || []).forEach((loc) => {
        //     (loc.orders || []).forEach((o) => {
        //         const key = `${(o.customer_first_name + " " + o.customer_last_name) || ""}|${o.customer_email || ""}`;
        //         if (!seen.has(key)) {
        //             seen.add(key);
        //             opts.push({
        //                 value: key,
        //                 label: `${(o.customer_first_name + " " + o.customer_last_name) || ""} (${o.customer_email || ""})`,
        //                 data: o
        //             });
        //         }
        //     });
        // });

        return opts;
    }, [orders]);

    // Extract unique locations
    const locationOptions = useMemo(() => {
        return (orders || []).map((loc) => ({
            value: loc.id,
            label: loc.name,
            data: loc
        }));
    }, [orders]);

    const resetForm = () => {
        setDate(new Date().toISOString().slice(0, 10));
        setAmount(0);
        setPaymentType("cod");
        setSelectedCustomer(null);
        setIsNewCustomer(false);
        setSelectedLocation(null);
        setCoupon("");
        setLineItems([]);
        setNewCustomer({
            first_name: "",
            last_name: "",
            customer_email: "",
            customer_phone: "",
            license_plates: "",
            car_models: "",
            company_name: ""
        });
    };

    const handleAdd = async () => {
        setIsLoading(true);
        try {
            const payload = {
                date,
                amount,
                paymentType,
                location: selectedLocation?.data || null,
                coupon,
                customer:
                    selectedCustomer?.value === "new"
                        ? newCustomer
                        : selectedCustomer?.data || null,
                line_items: lineItems.map((li) => ({
                    line_item_id: li.item?.value || null,
                    display_name: li.item?.label || null,
                    quantity: li.quantity,
                })),
            };
            await addMonthlyParkingSubscription(user.token, payload);
            handleSave();
            resetForm();
            toggle();
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancel = () => {
        resetForm();
        toggle();
    };

    const lineItemOptions = useMemo(() => {
        if (!selectedLocation) return [];

        const items = [];
        (selectedLocation.data?.orders || []).forEach((order) => {
            if (order.line_items_json) {
                try {
                    const parsed = JSON.parse(order.line_items_json);
                    parsed.forEach((item) => {
                        const typeMeta = (item.meta_data || []).find(
                            (m) => m.key === "type"
                        );
                        if (typeMeta && typeMeta.display_value) {
                            const unitPrice =
                                item.quantity && parseFloat(item.subtotal)
                                    ? parseFloat(item.subtotal) / item.quantity
                                    : 0;
                            items.push({
                                value: item.id,
                                label: typeMeta.display_value,
                                unitPrice: unitPrice,
                            });
                        }
                    });
                } catch (err) {
                    console.warn("Invalid JSON in line_items_json", err);
                }
            }
        });

        const seenLabels = new Set();
        return items.filter((i) => {
            if (seenLabels.has(i.label)) return false;
            seenLabels.add(i.label);
            return true;
        });
    }, [selectedLocation]);

    // Auto calculate total whenever quantities or selections change
    useEffect(() => {
        let total = 0;
        lineItems.forEach((li) => {
            if (li.item?.unitPrice && li.quantity > 0) {
                total += li.item.unitPrice * li.quantity;
            }
        });
        setAmount(total.toFixed(2));
    }, [lineItems]);

    return (
        <Modal isOpen={isOpen} toggle={handleCancel} size="lg">
            <ModalHeader toggle={handleCancel}>Add Monthly Parking Subscription</ModalHeader>
            <ModalBody>
                <Row>
                    <Col md={12}>
                        <Label>First Payment Date</Label>
                        <Input
                            type="date"
                            value={date}
                            onChange={(e) => setDate(e.target.value)}
                        />
                    </Col>
                </Row>

                <Row className="mt-3">
                    <Col md={6}>
                        <Label>Payment Type</Label>
                        <Input
                            type="select"
                            value={paymentType}
                            onChange={(e) => setPaymentType(e.target.value)}
                        >
                            <option value="cod">Cash</option>
                            <option value="cheque">Check</option>
                            <option value="qbo">QBO</option>
                            <option value="stripe_cc">Stripe Credit Card</option>
                            <option value="stripe_ach">Stripe ACH</option>
                            <option value="stripe_applepay">Stripe Apple Pay</option>
                        </Input>
                    </Col>
                    <Col md={6}>
                        <Label>Location</Label>
                        <Select
                            options={locationOptions}
                            value={selectedLocation}
                            onChange={(opt) => {
                                setSelectedLocation(opt);
                                const locItems = [];
                                (opt.data?.orders || []).forEach((order) => {
                                    if (order.line_items_json) {
                                        try {
                                            const parsed = JSON.parse(order.line_items_json);
                                            parsed.forEach((item) => {
                                                const typeMeta = (item.meta_data || []).find((m) => m.key === "type");
                                                if (typeMeta && typeMeta.display_value) {
                                                    locItems.push({
                                                        value: item.id,
                                                        label: typeMeta.display_value,
                                                        unitPrice:
                                                            item.quantity && parseFloat(item.subtotal)
                                                                ? parseFloat(item.subtotal) / item.quantity
                                                                : 0,
                                                    });
                                                }
                                            });
                                        } catch (err) { }
                                    }
                                });
                                if (locItems.length > 0) {
                                    setLineItems([{ item: locItems[0], quantity: 1 }]);
                                }
                            }}
                            isSearchable
                            styles={{
                                menu: (provided) => ({ ...provided, backgroundColor: "white", zIndex: 9999 })
                            }}
                        />
                    </Col>
                </Row>

                <Row className="mt-3">
                    <Col md={12}>
                        <Label>Customer</Label>
                        <Select
                            options={customerOptions}
                            value={selectedCustomer}
                            onChange={(opt) => {
                                setSelectedCustomer(opt);
                                setIsNewCustomer(opt.value === "new");
                            }}
                            isSearchable
                            styles={{
                                menu: (provided) => ({ ...provided, backgroundColor: "white", zIndex: 9999 })
                            }}
                        />
                    </Col>
                </Row>

                {isNewCustomer && (
                    <div className="mt-3 border rounded p-3 position-relative">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h6 className="mb-0">New Customer Details</h6>
                            <button
                                type="button"
                                className="btn-close"
                                aria-label="Close"
                                onClick={() => {
                                    setIsNewCustomer(false);
                                    setSelectedCustomer(null);
                                }}
                            />
                        </div>

                        <Row>
                            <Col md={6}>
                                <Label>First Name</Label>
                                <Input
                                    type="text"
                                    value={newCustomer.first_name}
                                    onChange={(e) => {
                                        const first_name = e.target.value.trim();
                                        const email =
                                            first_name && newCustomer.last_name
                                                ? `curbstand-${first_name.toLowerCase()}_${newCustomer.last_name.toLowerCase()}@curbstand.com`
                                                : newCustomer.customer_email;
                                        setNewCustomer({ ...newCustomer, first_name, customer_email: email });
                                    }}
                                />
                            </Col>
                            <Col md={6}>
                                <Label>Last Name</Label>
                                <Input
                                    type="text"
                                    value={newCustomer.last_name}
                                    onChange={(e) => {
                                        const last_name = e.target.value.trim();
                                        const email =
                                            newCustomer.first_name && last_name
                                                ? `curbstand-${newCustomer.first_name.toLowerCase()}_${last_name.toLowerCase()}@curbstand.com`
                                                : newCustomer.customer_email;
                                        setNewCustomer({ ...newCustomer, last_name, customer_email: email });
                                    }}
                                />
                            </Col>
                        </Row>
                        <Row className="mt-2">
                            <Col md={12}>
                                <Label>Email</Label>
                                <Input
                                    type="email"
                                    disabled
                                    value={newCustomer.customer_email}
                                    onChange={(e) =>
                                        setNewCustomer({ ...newCustomer, customer_email: e.target.value })
                                    }
                                />
                            </Col>
                        </Row>
                        <Row className="mt-2">
                            <Col md={6}>
                                <Label>License Plates</Label>
                                <Input
                                    type="text"
                                    value={newCustomer.license_plates}
                                    onChange={(e) =>
                                        setNewCustomer({ ...newCustomer, license_plates: e.target.value })
                                    }
                                />
                            </Col>
                            <Col md={6}>
                                <Label>Car Models</Label>
                                <Input
                                    type="text"
                                    value={newCustomer.car_models}
                                    onChange={(e) =>
                                        setNewCustomer({ ...newCustomer, car_models: e.target.value })
                                    }
                                />
                            </Col>
                        </Row>
                        <Row className="mt-2">
                            <Col md={6}>
                                <Label>Company Name</Label>
                                <Input
                                    type="text"
                                    value={newCustomer.company_name}
                                    onChange={(e) =>
                                        setNewCustomer({ ...newCustomer, company_name: e.target.value })
                                    }
                                />
                            </Col>
                            <Col md={6}>
                                <Label>Phone</Label>
                                <Input
                                    type="text"
                                    value={newCustomer.customer_phone}
                                    onChange={(e) =>
                                        setNewCustomer({ ...newCustomer, customer_phone: e.target.value })
                                    }
                                />
                            </Col>
                        </Row>
                    </div>
                )}

                {/* Dynamic Line Items Section */}
                {selectedLocation && (
                    <div className="mt-4 border rounded p-3">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h6 className="mb-0">Products</h6>
                            <Button
                                color="success"
                                size="sm"
                                onClick={() =>
                                    setLineItems((prev) => [...prev, { quantity: 0, item: null }])
                                }
                            >
                                + Add Product
                            </Button>
                        </div>

                        {lineItems.map((li, index) => (
                            <Row key={index} className="align-items-center mb-2">
                                <Col md={8}>
                                    <Select
                                        options={lineItemOptions}
                                        value={li.item}
                                        onChange={(opt) => {
                                            setLineItems((prev) =>
                                                prev.map((p, i) => (i === index ? { ...p, item: opt } : p))
                                            );
                                        }}
                                        placeholder="Select Product"
                                        styles={{
                                            menu: (provided) => ({
                                                ...provided,
                                                backgroundColor: "white",
                                                zIndex: 9999,
                                            }),
                                        }}
                                    />
                                </Col>
                                <Col md={3}>
                                    <div className="d-flex align-items-center">
                                        <Button
                                            color="light"
                                            size="sm"
                                            onClick={() =>
                                                setLineItems((prev) =>
                                                    prev.map((p, i) =>
                                                        i === index
                                                            ? { ...p, quantity: Math.max(0, (p.quantity || 0) - 1) }
                                                            : p
                                                    )
                                                )
                                            }
                                        >
                                            −
                                        </Button>
                                        <Input
                                            type="number"
                                            min="0"
                                            className="mx-2 text-center"
                                            value={li.quantity}
                                            onChange={(e) => {
                                                const qty = parseFloat(e.target.value) || 0;
                                                setLineItems((prev) =>
                                                    prev.map((p, i) => (i === index ? { ...p, quantity: qty } : p))
                                                );
                                            }}
                                        />
                                        <Button
                                            color="light"
                                            size="sm"
                                            onClick={() =>
                                                setLineItems((prev) =>
                                                    prev.map((p, i) =>
                                                        i === index
                                                            ? { ...p, quantity: (p.quantity || 0) + 1 }
                                                            : p
                                                    )
                                                )
                                            }
                                        >
                                            +
                                        </Button>
                                    </div>
                                </Col>
                                <Col md={1} className="text-center">
                                    <i
                                        className="bx bx-trash"
                                        style={{ cursor: "pointer", color: "red", fontSize: "20px" }}
                                        onClick={() =>
                                            setLineItems((prev) => prev.filter((_, i) => i !== index))
                                        }
                                    ></i>
                                </Col>
                            </Row>
                        ))}

                        <Row className="mt-3">
                            <Col md={12}>
                                <Label>Coupon (optional)</Label>
                                <Input
                                    type="text"
                                    placeholder="Enter coupon code"
                                    value={coupon}
                                    onChange={(e) => setCoupon(e.target.value)}
                                />
                            </Col>
                        </Row>

                        <Row className="mt-3">
                            <Col md={12}>
                                <Label>Monthly Amount</Label>
                                <InputGroup>
                                    <InputGroupText>$</InputGroupText>
                                    <Input
                                        type="number"
                                        value={amount}
                                        disabled
                                    />
                                </InputGroup>
                            </Col>
                        </Row>

                    </div>
                )}
            </ModalBody>
            <ModalFooter>
                <Button color="primary" onClick={handleAdd} disabled={isLoading}>
                    {isLoading ? (
                        <>
                            <span
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                                aria-hidden="true"
                            ></span>
                            Processing...
                        </>
                    ) : (
                        "Add"
                    )}
                </Button>
                <Button color="secondary" onClick={handleCancel}>
                    Cancel
                </Button>
            </ModalFooter>
        </Modal>
    );
};

AddMonthlyParkingSubscriptionModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    toggle: PropTypes.func.isRequired,
    orders: PropTypes.array
};

export default AddMonthlyParkingSubscriptionModal;